import * as Sentry from "@sentry/react";
import { encodeBase64Url } from "../../../utils";

/**
 * Base path for the silver-resource details page. Single source of truth —
 * imported by the URL builder below, the route declaration in `App.js`, and
 * the unit tests so a typo in the constant gets caught at the route boundary.
 *
 * Does NOT include the `:id` route param — concatenate `/:id` at the route
 * declaration and `/${encodedResourceId}` at the call site.
 */
export const SILVER_RESOURCE_BASE_PATH = "/resources/list/silver";

export interface SilverResourceLinkParts {
  resource_id?: string | null;
  generator_id?: string | null;
  datasource_id_str?: string | null;
}

/**
 * Module-scope dedupe set for Sentry encoding-failure reports.
 *
 * `buildSilverResourceUrl` is called from a TanStack Table `cell:` callback,
 * which runs once per row, per render. Without dedupe, a single row whose
 * `resource_id` (or `generator_id` / `datasource_id_str`) fails base64-url
 * encoding would spam Sentry on every re-render — paginate, sort, search,
 * anything that re-renders the table.
 *
 * Keys are `${paramKind}::${rawValue}` so the same raw string failing as two
 * different param kinds still gets one report per kind. Lifetime is the JS
 * module instance (effectively per session / page load).
 */
const reportedEncodingFailures = new Set<string>();

/** Test-only — reset the dedupe set between tests. */
export const __resetReportedEncodingFailuresForTesting = (): void => {
  reportedEncodingFailures.clear();
};

/**
 * Build the URL for the silver-resource details page.
 *
 * - The path `resource_id` is base64-url-encoded so it matches the format the
 *   provider decodes via `decodeBase64Url`.
 * - Query params (`generator_id`, `datasource_id_str`) are also base64-url-
 *   encoded — they may contain customer-identifying values we don't want to
 *   leak in plain text in the URL. The provider safely decodes them and the
 *   backend falls back to the legacy lookup if either is missing.
 * - If `generator_id` or `datasource_id_str` is missing/empty, the backend will
 *   silently fall back to the legacy `resource_id`-only lookup. We still link
 *   to the row in that case so the user isn't blocked by a data-quality issue.
 *
 * Returns `null` when there is no `resource_id` to link to, or when encoding
 * the `resource_id` fails (e.g. `btoa` rejects a non-Latin1 character) — the
 * caller renders the row as plain text in that case. Failures to encode the
 * query-string params are reported to Sentry and the offending param is
 * dropped, since the backend can fall back to a legacy lookup.
 *
 * The optional `opId` (sourced from the caller's parent GraphQL query — the
 * same value the backend received as the `op-id` header) is forwarded into
 * every Sentry `extra` block so FE-side encoding failures can be correlated
 * with the originating backend request.
 */
export const buildSilverResourceUrl = (
  { resource_id, generator_id, datasource_id_str }: SilverResourceLinkParts,
  opId?: string
): string | null => {
  if (!resource_id) {
    return null;
  }

  let encodedResourceId: string;
  try {
    encodedResourceId = encodeBase64Url(resource_id);
  } catch (error) {
    // Dedupe per-session — see `reportedEncodingFailures` doc above.
    // PII guard — never put the raw resource_id in `extra`; only its length.
    const dedupeKey = `resource_id::${resource_id}`;
    if (!reportedEncodingFailures.has(dedupeKey)) {
      reportedEncodingFailures.add(dedupeKey);
      Sentry.captureException(error, {
        extra: {
          action: "buildSilverResourceUrl",
          message: "Failed to base64-url-encode silver resource_id",
          resource_id_length: resource_id.length,
          ...(opId !== undefined && { opId }),
        },
      });
    }
    return null;
  }

  const safeEncodeParam = (value: string, paramName: string): string | null => {
    try {
      return encodeBase64Url(value);
    } catch (error) {
      // Dedupe per-session — see `reportedEncodingFailures` doc above.
      // PII guard — never put the raw param value in `extra`; only its length.
      const dedupeKey = `${paramName}::${value}`;
      if (!reportedEncodingFailures.has(dedupeKey)) {
        reportedEncodingFailures.add(dedupeKey);
        Sentry.captureException(error, {
          extra: {
            action: "buildSilverResourceUrl",
            message: `Failed to base64-url-encode silver ${paramName}`,
            value_length: value.length,
            ...(opId !== undefined && { opId }),
          },
        });
      }
      return null;
    }
  };

  const params = new URLSearchParams();

  if (generator_id) {
    const encoded = safeEncodeParam(generator_id, "generator_id");
    if (encoded !== null) {
      params.set("generator_id", encoded);
    }
  }
  if (datasource_id_str) {
    const encoded = safeEncodeParam(datasource_id_str, "datasource_id_str");
    if (encoded !== null) {
      params.set("datasource_id_str", encoded);
    }
  }

  const query = params.toString();
  const path = `${SILVER_RESOURCE_BASE_PATH}/${encodedResourceId}`;
  return query ? `${path}?${query}` : path;
};
